import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import ReceiptDialog from '../../components/ReceiptDialog';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useCart } from '../../contexts/CartContext';
import { cancelOrder, getOrder, getOrders, unparkOrder, voidOrderItem } from '../../lib/api';
import {
  extractCustomerOrderNotes,
  getOrderDisplayName,
  getOrderFulfillmentMeta,
  getOrderSourceMeta,
  isOrderUnpaid,
  isPickupOrder,
} from '../orderMeta';

const FILTERS = [
  { value: 'all', label: 'Alle offen' },
  { value: 'open', label: 'Neu / offen' },
  { value: 'parked', label: 'Geparkt' },
];

function StatusPill({ status }) {
  const map = {
    open: { label: 'Offen', cls: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Bezahlt', cls: 'bg-emerald-100 text-emerald-700' },
    cancelled: { label: 'Storno', cls: 'bg-red-100 text-red-600' },
    parked: { label: 'Geparkt', cls: 'bg-amber-100 text-amber-700' },
  };
  const current = map[status] || map.open;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${current.cls}`}>
      {current.label}
    </span>
  );
}

function StatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  if (status === 'cancelled') return <XCircle className="h-5 w-5 text-red-400" />;
  if (status === 'parked') return <Clock className="h-5 w-5 text-amber-500" />;
  return <Clock className="h-5 w-5 text-blue-400" />;
}

function MetaBadge({ className, label }) {
  return (
    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${className}`}>
      {label}
    </span>
  );
}

function formatDateTime(value) {
  if (!value) return '–';
  return format(new Date(value), "dd.MM.yyyy 'um' HH:mm", { locale: de });
}

function formatTime(value) {
  if (!value) return '–';
  return new Date(value).toLocaleTimeString('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getOrderItemKey(item, index = 0) {
  return item?.cartKey || item?.product_id || `order-item-${index}`;
}

function getActiveOrderItems(order) {
  return (order?.items || []).filter((item) => !item?.voided);
}

function CancelOrderDialog({
  open,
  onOpenChange,
  order,
  cancelMode,
  onCancelModeChange,
  selectedItemKeys,
  onToggleItem,
  onToggleAllItems,
  reason,
  onReasonChange,
  onConfirm,
  isCancelling,
  isPreparing,
}) {
  const activeItems = getActiveOrderItems(order);
  const allSelected = activeItems.length > 0
    && activeItems.every((item, index) => selectedItemKeys.includes(getOrderItemKey(item, index)));
  const confirmDisabled = isCancelling
    || isPreparing
    || reason.trim().length < 6
    || (cancelMode === 'items' && selectedItemKeys.length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Storno dokumentieren</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Storno wird im Tagesabschluss protokolliert</p>
                <p className="mt-1 text-xs text-amber-700">
                  Bitte gib einen klaren Grund ein. Diese Information bleibt für Berichtswesen und Nachvollziehbarkeit erhalten.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Was soll storniert werden?</p>
                <p className="text-xs text-muted-foreground">
                  Bestellung #{order?.order_number?.split('-')[1] ?? order?.order_number}
                  {order?.table_name ? ` · ${order.table_name}` : ''}
                </p>
              </div>
              {cancelMode === 'items' && activeItems.length > 0 ? (
                <button
                  type="button"
                  onClick={onToggleAllItems}
                  className="text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700"
                >
                  {allSelected ? 'Auswahl aufheben' : 'Alle Artikel wählen'}
                </button>
              ) : null}
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <button
                type="button"
                onClick={() => onCancelModeChange('items')}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${
                  cancelMode === 'items'
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold">Artikel auswählen</p>
                  <p className="mt-1 text-xs opacity-80">Nur einzelne Positionen stornieren</p>
                </div>
                {cancelMode === 'items' ? <CheckCircle2 className="h-4 w-4" /> : null}
              </button>

              <button
                type="button"
                onClick={() => onCancelModeChange('full')}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${
                  cancelMode === 'full'
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold">Ganze Bestellung</p>
                  <p className="mt-1 text-xs opacity-80">Komplette offene Bestellung stornieren</p>
                </div>
                {cancelMode === 'full' ? <CheckCircle2 className="h-4 w-4" /> : null}
              </button>
            </div>

            {cancelMode === 'items' ? (
              <div className="rounded-2xl border border-border bg-muted/70">
                <div className="border-b border-border px-4 py-3">
                  <p className="text-sm font-medium text-muted-foreground">Artikelweise stornieren</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Wähle die Positionen aus, die storniert werden sollen.
                  </p>
                </div>

                <div className="max-h-72 overflow-y-auto">
                  {isPreparing ? (
                    <div className="flex items-center justify-center px-4 py-10">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : activeItems.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                      Keine aktiven Artikel verfügbar
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200">
                      {activeItems.map((item, index) => {
                        const itemKey = getOrderItemKey(item, index);
                        const isSelected = selectedItemKeys.includes(itemKey);

                        return (
                          <button
                            key={itemKey}
                            type="button"
                            onClick={() => onToggleItem(itemKey)}
                            className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
                              isSelected ? 'bg-blue-50' : 'bg-card hover:bg-muted'
                            }`}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center border text-[11px] font-bold ${
                                isSelected
                                  ? 'border-blue-200 bg-blue-600 text-white'
                                  : 'border-border bg-card text-muted-foreground'
                              }`}>
                                {item.quantity}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-foreground">{item.product_name}</p>
                                {item.notes ? (
                                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.notes}</p>
                                ) : null}
                              </div>
                            </div>
                            <span className="flex-shrink-0 text-sm font-semibold tabular-nums text-muted-foreground">
                              €{(item.price * item.quantity).toFixed(2)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <label htmlFor="storno-reason" className="text-sm font-medium text-muted-foreground">
              Storno-Grund
            </label>
            <Textarea
              id="storno-reason"
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              placeholder="Zum Beispiel: Gast hat vor Ausgabe storniert / falscher Tisch / Artikel falsch boniert"
              className="min-h-[120px] resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="flex h-11 flex-1 items-center justify-center rounded-xl border border-border text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              Abbrechen
            </button>
            <button
              onClick={onConfirm}
              disabled={confirmDisabled}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              {cancelMode === 'full' ? 'Bestellung stornieren' : 'Artikel stornieren'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetail({ order, onReceipt, onCancel, onWeiterBestellen, onGoToPayment }) {
  if (!order) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="text-center text-muted-foreground">
          <FileText className="mx-auto mb-3 h-14 w-14 opacity-20" />
          <p className="text-sm">Bestellung auswählen</p>
        </div>
      </div>
    );
  }

  const sourceMeta = getOrderSourceMeta(order.order_source);
  const fulfillmentMeta = getOrderFulfillmentMeta(order);
  const paymentLabel = isPickupOrder(order) ? 'Abholung kassieren' : 'Zur Zahlung';
  const isUnpaid = isOrderUnpaid(order);
  const customerNotes = extractCustomerOrderNotes(order.notes);
  const activeItems = getActiveOrderItems(order);
  const displayName = getOrderDisplayName(order);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-border p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="mb-0.5 text-xs text-muted-foreground">Bestellnummer</p>
            <p className="text-lg font-bold leading-tight text-foreground">
              #{order.order_number?.split('-')[1] ?? order.order_number}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{displayName}</p>
          </div>
          <StatusPill status={order.status} />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
          <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
        </div>

        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          <p>Angelegt: {formatDateTime(order.created_at)}</p>
          {order.completed_at && <p>Bezahlt: {formatDateTime(order.completed_at)}</p>}
          {order.cancelled_at && <p>Storniert: {formatDateTime(order.cancelled_at)}</p>}
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {customerNotes ? (
          <div className="rounded-2xl border border-border bg-muted p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notiz</p>
            <p className="text-sm text-muted-foreground">{customerNotes}</p>
          </div>
        ) : null}

        <div className="space-y-2">
          {activeItems.map((item, index) => (
            <div
              key={`${order.id}-${getOrderItemKey(item, index)}`}
              className="rounded-2xl border border-border bg-card p-3 shadow-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                    {item.quantity}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{item.product_name}</p>
                    {item.notes ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">+ {item.notes}</p>
                    ) : null}
                  </div>
                </div>
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  €{(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-border p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="font-semibold text-muted-foreground">Gesamt</span>
          <span className="text-xl font-bold tabular-nums text-foreground">€{order.total?.toFixed(2)}</span>
        </div>

        {order.payment_method ? (
          <p className="mb-3 text-sm text-muted-foreground">
            Zahlung: {order.payment_method === 'cash' ? 'Bargeld' : 'Karte / NFC'}
          </p>
        ) : null}

        <div className="space-y-2">
          {order.status === 'completed' && (
            <button
              onClick={() => onReceipt(order.id)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-muted text-sm font-medium text-muted-foreground transition-colors hover:bg-slate-200"
            >
              <FileText className="h-4 w-4" />
              Beleg öffnen
            </button>
          )}

          {isUnpaid && (
            <>
              <div className="grid gap-2 md:grid-cols-2">
                <button
                  onClick={() => onWeiterBestellen(order)}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-50 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                >
                  <Plus className="h-4 w-4" />
                  Weiter bestellen
                </button>
                <button
                  onClick={() => onGoToPayment(order)}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-50 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                >
                  <CreditCard className="h-4 w-4" />
                  {paymentLabel}
                </button>
              </div>

              <div className="rounded-2xl border border-border bg-muted p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Storno nur mit Dokumentation</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Nach dem Senden bleibt der Storno im Berichtswesen und Tagesabschluss sichtbar.
                    </p>
                  </div>
                  <button
                    onClick={() => onCancel(order)}
                    className="flex h-9 items-center justify-center gap-2 rounded-xl border border-red-200 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                    Storno
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order, selected, onSelect }) {
  const sourceMeta = getOrderSourceMeta(order.order_source);
  const fulfillmentMeta = getOrderFulfillmentMeta(order);
  const activeItemCount = getActiveOrderItems(order).length;
  const displayName = getOrderDisplayName(order);

  return (
    <button
      type="button"
      onClick={() => onSelect(order)}
      className={`w-full border-b border-border text-left transition-colors ${
        selected ? 'bg-muted' : 'hover:bg-muted'
      }`}
    >
      <div className="space-y-2 px-4 py-4 lg:hidden">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <StatusIcon status={order.status} />
            <div className="min-w-0">
              <p className="text-lg font-bold leading-tight text-foreground">
                #{order.order_number?.split('-')[1] ?? order.order_number}
              </p>
              <p className="mt-1 truncate text-sm font-medium text-muted-foreground">{displayName}</p>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-start gap-2">
            <p className="text-lg font-bold tabular-nums text-foreground">€{order.total?.toFixed(2)}</p>
            <ChevronRight className="mt-1 h-4 w-4 text-slate-300" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <StatusPill status={order.status} />
          <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
          <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
        </div>

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <p className="truncate">{activeItemCount} Artikel</p>
          <p className="flex-shrink-0">{formatTime(order.created_at)}</p>
        </div>
      </div>

      <div className="hidden items-center gap-3 px-4 py-3 lg:flex">
        <StatusIcon status={order.status} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-foreground">
              #{order.order_number?.split('-')[1] ?? order.order_number}
            </p>
            <p className="text-sm font-bold tabular-nums text-muted-foreground">
              €{order.total?.toFixed(2)}
            </p>
          </div>

          <div className="mt-1 flex flex-wrap gap-1">
            <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
            <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
          </div>

          <div className="mt-1 flex items-center justify-between gap-1">
            <p className="truncate text-xs text-muted-foreground">
              {displayName} · {activeItemCount} Artikel
            </p>
            <p className="flex-shrink-0 text-[10px] text-muted-foreground">
              {formatTime(order.created_at)}
            </p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-300" />
      </div>
    </button>
  );
}

export default function PosOrders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelMode, setCancelMode] = useState('items');
  const [selectedCancelItemKeys, setSelectedCancelItemKeys] = useState([]);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isPreparingCancel, setIsPreparingCancel] = useState(false);

  const navigate = useNavigate();
  const { loadOrder } = useCart();

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const [openOrders, parkedOrders] = await Promise.all([
        getOrders('open', null),
        getOrders('parked', null),
      ]);
      const data = [...openOrders, ...parkedOrders];
      const filtered = data.filter((order) => {
        if (!isOrderUnpaid(order)) return false;
        if (statusFilter !== 'all' && order.status !== statusFilter) return false;
        return true;
      });
      setOrders(filtered);
      const nextSelection = filtered.find((order) => order.id === selectedOrder?.id) || filtered[0] || null;
      setSelectedOrder(nextSelection);
      setDetailOrder(nextSelection);
    } catch {
      toast.error('Offene Bestellungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrder?.id, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleSelect = (order) => {
    setSelectedOrder(order);
    setDetailOrder(order);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowMobileDetail(true);
    }
  };

  const handleReceipt = async (orderId) => {
    try {
      const full = await getOrder(orderId);
      setDetailOrder(full);
      setShowReceipt(true);
    } catch {
      toast.error('Beleg konnte nicht geladen werden');
    }
  };

  const loadOrderForTerminal = async (order) => {
    if (order.status === 'parked') {
      await unparkOrder(order.id);
      toast.success('Geparkte Bestellung wieder aktiviert');
    }

    return getOrder(order.id);
  };

  const handleWeiterBestellen = async (order) => {
    try {
      const full = await loadOrderForTerminal(order);
      loadOrder(full);
      navigate('/pos/terminal');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bestellung konnte nicht geladen werden');
    }
  };

  const handleGoToPayment = async (order) => {
    try {
      const full = await loadOrderForTerminal(order);
      loadOrder(full);
      navigate('/pos/terminal', {
        state: {
          openPayment: true,
          paymentIntentId: `${full.id}-${Date.now()}`,
          fulfillmentType: full.fulfillment_type,
          paymentOrder: full,
        },
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bestellung konnte nicht zur Zahlung geöffnet werden');
    }
  };

  const openCancelDialog = async (order) => {
    setCancelTarget(order);
    setCancelMode('items');
    setSelectedCancelItemKeys([]);
    setCancelReason('');
    setIsPreparingCancel(true);
    try {
      const full = await getOrder(order.id);
      setCancelTarget(full);
    } catch {
      toast.error('Bestellung konnte für Storno nicht vollständig geladen werden');
    } finally {
      setIsPreparingCancel(false);
    }
  };

  const toggleCancelItem = (itemKey) => {
    setSelectedCancelItemKeys((current) => (
      current.includes(itemKey)
        ? current.filter((key) => key !== itemKey)
        : [...current, itemKey]
    ));
  };

  const toggleAllCancelItems = () => {
    const activeItems = getActiveOrderItems(cancelTarget);
    const keys = activeItems.map((item, index) => getOrderItemKey(item, index));
    const allSelected = keys.length > 0 && keys.every((key) => selectedCancelItemKeys.includes(key));
    setSelectedCancelItemKeys(allSelected ? [] : keys);
  };

  const handleCancel = async () => {
    if (!cancelTarget || cancelReason.trim().length < 6) return;

    const activeItems = getActiveOrderItems(cancelTarget);
    const selectedItems = activeItems.filter((item, index) => (
      selectedCancelItemKeys.includes(getOrderItemKey(item, index))
    ));
    const shouldCancelWholeOrder = cancelMode === 'full'
      || (cancelMode === 'items' && activeItems.length > 0 && selectedItems.length === activeItems.length);

    if (!shouldCancelWholeOrder && selectedItems.length === 0) {
      toast.error('Bitte mindestens einen Artikel auswählen');
      return;
    }

    setIsCancelling(true);
    try {
      if (shouldCancelWholeOrder) {
        await cancelOrder(cancelTarget.id, cancelReason.trim());
        toast.success('Bestellung storniert');
      } else {
        for (const item of selectedItems) {
          await voidOrderItem(cancelTarget.id, getOrderItemKey(item), cancelReason.trim());
        }
        toast.success(`${selectedItems.length} Artikel storniert`);
      }

      setCancelTarget(null);
      setCancelMode('items');
      setSelectedCancelItemKeys([]);
      setCancelReason('');
      await loadOrders();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Storno fehlgeschlagen');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="flex h-full overflow-hidden bg-muted" data-testid="pos-orders">
      <div className="flex w-full flex-shrink-0 flex-col border-r border-border bg-card lg:w-96">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <div className="flex flex-1 gap-1 overflow-x-auto">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <button
            onClick={loadOrders}
            className="rounded-lg p-1.5 transition-colors hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="border-b border-border px-4 py-2">
          <p className="text-xs text-muted-foreground">
            {isLoading ? 'Lädt…' : `${orders.length} offene Bestellung${orders.length === 1 ? '' : 'en'}`}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-muted-foreground">
              <FileText className="mb-2 h-10 w-10 opacity-30" />
              <p className="text-sm">Keine offenen Bestellungen</p>
            </div>
          ) : (
            orders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                selected={selectedOrder?.id === order.id}
                onSelect={handleSelect}
              />
            ))
          )}
        </div>
      </div>

      <div className="hidden flex-1 flex-col overflow-hidden bg-card lg:flex">
        <OrderDetail
          order={detailOrder}
          onReceipt={handleReceipt}
          onCancel={openCancelDialog}
          onWeiterBestellen={handleWeiterBestellen}
          onGoToPayment={handleGoToPayment}
        />
      </div>

      <ReceiptDialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        order={detailOrder}
      />

      <Dialog open={showMobileDetail} onOpenChange={setShowMobileDetail}>
        <DialogContent className="h-[92vh] max-w-[96vw] overflow-hidden p-0 lg:hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Bestelldetails</DialogTitle>
          </DialogHeader>
          <OrderDetail
            order={detailOrder}
            onReceipt={handleReceipt}
            onCancel={openCancelDialog}
            onWeiterBestellen={handleWeiterBestellen}
            onGoToPayment={handleGoToPayment}
          />
        </DialogContent>
      </Dialog>

      <CancelOrderDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null);
            setCancelMode('items');
            setSelectedCancelItemKeys([]);
            setCancelReason('');
          }
        }}
        order={cancelTarget}
        cancelMode={cancelMode}
        onCancelModeChange={setCancelMode}
        selectedItemKeys={selectedCancelItemKeys}
        onToggleItem={toggleCancelItem}
        onToggleAllItems={toggleAllCancelItems}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onConfirm={handleCancel}
        isCancelling={isCancelling}
        isPreparing={isPreparingCancel}
      />
    </div>
  );
}
